import { setCookie } from 'cookies-next'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'
import { FC, ReactNode, useEffect } from 'react'

import { useAuth } from '@/api/models/auth/hooks/useAuth'
import { AUTH_REDIRECTED_FROM_KEY } from '@/api/models/auth/lib/auth.constants'
import {
  RefreshSessionStatus,
  useServiceErrorsStore
} from '@/components/entities/common/model/service-errors.store'
import { useSetSentryUser } from '@/utils/sentry'

type Props = {
  children: ReactNode
  disableRedirect?: boolean
}

export const AuthorizedContent: FC<Props> = ({ children, disableRedirect }) => {
  const { user, userLoading } = useAuth()
  const refreshSessionStatus = useServiceErrorsStore(
    state => state.refreshSessionStatus
  )
  const isRefreshInProgress = refreshSessionStatus !== RefreshSessionStatus.None

  const pathname = usePathname()
  const router = useRouter()

  useSetSentryUser({ userLoading, user })

  useEffect(() => {
    const emptyPath = '/'

    if (!userLoading && !user && !disableRedirect && !isRefreshInProgress) {
      const loginPath = '/auth/signin'
      setCookie(AUTH_REDIRECTED_FROM_KEY, router.asPath)
      const parsedUrl = new URL(window.location.href, window.location.origin)
      const type = parsedUrl.searchParams.get('type')
      const premiseId = parsedUrl.searchParams.get('premiseId')

      const redirectParam = type || premiseId

      if (pathname && pathname !== emptyPath) {
        const queryKey =
          redirectParam === type ? 'type=' + type : 'premiseId=' + premiseId
        const redirectUrl = redirectParam ? `${pathname}?${queryKey}` : pathname
        router.replace(
          `${loginPath}?redirect=${encodeURIComponent(redirectUrl)}`
        )
      } else {
        router.replace(loginPath)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading, isRefreshInProgress])

  return <>{(user || disableRedirect || isRefreshInProgress) && children}</>
}
